import { capitalize } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'

import {
  getColorForStage,
  formatFieldWithMetadata,
  formatTypePropertyWithMetadata
} from 'components/alfi/alfi-utils'
import Text from 'components/common/Text'
import duration from 'duration'
import theme from 'theme'
import { last } from 'utils/list'

import {
  PanelHeader,
  PanelTitle,
  PanelDetails,
  PanelRow,
  Label,
  Value
} from '../../attacks/details/AttackStyles'

const dotSize = theme.space[3]

function ServiceStateTableKey({ children, serviceStateColor }) {
  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space[3],
        gridTemplateColumns: `${theme.space[3]}px 1fr`,
        alignItems: 'center'
      }}
    >
      <div
        css={{
          display: 'inline-block',
          height: dotSize,
          width: dotSize,
          backgroundColor: serviceStateColor,
          borderRadius: theme.borderRadius[2]
        }}
      />
      {children}
    </div>
  )
}

ServiceStateTableKey.propTypes = {
  children: PropTypes.node,
  serviceStateColor: PropTypes.string
}

function ClientTag({ client, impacted }) {
  return (
    <Value
      css={{
        display: 'grid',
        gap: theme.space[3],
        gridTemplateColumns: `${theme.space[3]}px 1fr`,
        alignItems: 'center',

        '&:not(:last-of-type)': {
          marginBottom: theme.space[3]
        }
      }}
    >
      <div
        css={{
          display: 'inline-block',
          borderRadius: theme.borderRadius[2],
          backgroundColor: impacted
            ? theme.colors.blue[0]
            : theme.colors.gray[2],
          width: dotSize,
          height: dotSize
        }}
      />
      <div>
        {client}
        <Text as="small" css={{ marginTop: theme.space[1] }}>
          {impacted ? 'Impacted' : 'Not Impacted'}
        </Text>
      </div>
    </Value>
  )
}

ClientTag.propTypes = {
  children: PropTypes.node,
  impacted: PropTypes.bool,
  className: PropTypes.string,
  client: PropTypes.string
}

/**
 *
 * @param       {[type]} attack
 * @param       {Object} [metadata={} }]
 * @constructor
 */
export default function AlfiAttack({ attack, metadata = {} }) {
  const {
    duration: attackDuration,
    created_at,
    created_by,
    create_source,
    end_time,
    service_states,
    application_scope,
    traffic,
    distributed = [],
    impacted = []
  } = attack

  const { applicationQuery, trafficQuery, trafficImpact } = metadata

  const appQuery = application_scope[0]
  const { query, impact } = traffic[0]
  const latestStage = last(service_states).stage
  const formattedLatestStage = latestStage.split(/(?=[A-Z])/).join(' ')

  return (
    <div
      css={{
        display: 'grid',
        gridColumnGap: theme.space[7],
        gridRowGap: theme.space[7],
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        paddingTop: theme.space[3],
        paddingBottom: theme.space[5],
        marginBottom: theme.space[5]
      }}
    >
      <div>
        <PanelHeader>
          <PanelTitle>Details</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          <PanelRow>
            <Label>Current State</Label>
            <Value>
              <ServiceStateTableKey
                serviceStateColor={getColorForStage(latestStage)}
              >
                {formattedLatestStage}
              </ServiceStateTableKey>
            </Value>
          </PanelRow>
          <PanelRow>
            <Label>Created Time</Label>
            <Value>
              {capitalize(moment(created_at).fromNow())}{' '}
              <Text as="small" css={{ marginTop: theme.space[1] }}>
                {moment(created_at).format('M/D/YYYY h:mm a')}
              </Text>
            </Value>
          </PanelRow>
          {end_time && (
            <PanelRow>
              <Label>End Time</Label>
              <Value>
                {capitalize(moment(end_time).fromNow())}{' '}
                <Text as="small" css={{ marginTop: theme.space[1] }}>
                  {moment(end_time).format('M/D/YYYY h:mm a')}
                </Text>
              </Value>
            </PanelRow>
          )}
          <PanelRow>
            <Label>User</Label>
            <Value>{created_by}</Value>
          </PanelRow>
          <PanelRow>
            <Label>Duration</Label>
            <Value>
              {duration(attackDuration)}

              {/*display the length in seconds as well if it is greater than a minute*/}
              {attackDuration >= 60 && (
                <Text
                  as="small"
                  css={{ marginTop: theme.space[1] }}
                >{`${attackDuration} ${
                  attackDuration !== 1 ? 'seconds' : 'second'
                }`}</Text>
              )}
            </Value>
          </PanelRow>
          <PanelRow>
            <Label>Source</Label>
            <Value>{create_source}</Value>
          </PanelRow>
        </PanelDetails>
      </div>
      <div>
        <PanelHeader>
          <PanelTitle>Service States</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          {service_states.map(({ stage, timestamp }) => {
            return (
              <PanelRow key={stage}>
                <Label>
                  <ServiceStateTableKey
                    serviceStateColor={getColorForStage(stage)}
                  >
                    {stage.split(/(?=[A-Z])/).join(' ')}
                  </ServiceStateTableKey>
                </Label>
                <Value>
                  {capitalize(moment(timestamp).fromNow())}
                  <Text as="small" css={{ marginTop: theme.space[1] }}>
                    {moment(timestamp).format('M/D/YYYY h:mm a')}
                  </Text>
                </Value>
              </PanelRow>
            )
          })}
        </PanelDetails>
      </div>
      {/* Application */}
      <div>
        <PanelHeader>
          <PanelTitle>Application</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          <PanelRow>
            <Label>Type</Label>
            <Value>
              {formatTypePropertyWithMetadata(appQuery, applicationQuery)}
            </Value>
          </PanelRow>
          {Number.isFinite(appQuery.percentage_to_impact) && (
            <PanelRow>
              <Label>Percentage to Impact</Label>
              <Value>{`${appQuery.percentage_to_impact * 100}%`}</Value>
            </PanelRow>
          )}
          {Object.keys(appQuery.fields).map((field) => {
            const { key, value } = formatFieldWithMetadata(
              field,
              appQuery.fields[field],
              appQuery,
              applicationQuery
            )
            return (
              <PanelRow key={field}>
                <Label>{key}</Label>
                <Value>{value}</Value>
              </PanelRow>
            )
          })}
        </PanelDetails>
      </div>

      {/* Traffic*/}
      <div>
        <PanelHeader>
          <PanelTitle>Traffic</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          <PanelRow>
            <Label>Type</Label>
            <Value>{formatTypePropertyWithMetadata(query, trafficQuery)}</Value>
          </PanelRow>
          {Number.isFinite(query.percentage_to_impact) && (
            <PanelRow>
              <Label>Percentage to Impact</Label>
              <Value>{`${query.percentage_to_impact * 100}%`}</Value>
            </PanelRow>
          )}
          {Object.keys(query.fields).map((field) => {
            const { key, value } = formatFieldWithMetadata(
              field,
              query.fields[field],
              query,
              trafficQuery
            )
            return (
              <PanelRow key={field}>
                <Label>{key}</Label>
                <Value>{value}</Value>
              </PanelRow>
            )
          })}
        </PanelDetails>
      </div>
      {/* Impact*/}
      <div>
        <PanelHeader>
          <PanelTitle>Impact</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          <PanelRow>
            <Label>Type</Label>
            <Value>{impact.type}</Value>
          </PanelRow>
          {Object.keys(impact.fields).map((field) => {
            const { key, value } = formatFieldWithMetadata(
              field,
              impact.fields[field],
              impact,
              trafficImpact
            )
            return (
              <PanelRow key={field}>
                <Label>{key}</Label>
                <Value>{value}</Value>
              </PanelRow>
            )
          })}
        </PanelDetails>
      </div>
      {/* Distributed/Impacted*/}
      <div>
        <PanelHeader>
          <PanelTitle>Matched Clients</PanelTitle>
        </PanelHeader>
        <PanelDetails>
          {distributed.map((clientName) => {
            return (
              <ClientTag
                key={clientName}
                client={clientName}
                impacted={impacted.indexOf(clientName) > -1}
              />
            )
          })}
        </PanelDetails>
      </div>
    </div>
  )
}

AlfiAttack.propTypes = {
  attack: PropTypes.object,
  metadata: PropTypes.object
}
